/**
 * ClienteFormModal — Modal reutilizable para crear / editar un cliente.
 * Usado tanto en la página de Clientes (Ingeniería) como inline en el editor
 * de presupuestos (BudgetForm). Persiste contra /api/listas/clientes.
 *
 * Props:
 *  - initial: cliente existente para editar (null = nuevo)
 *  - onClose(): cerrar sin guardar
 *  - onSaved(cliente): callback con el cliente creado/actualizado
 */
import { useState } from "react";
import ClienteIdentidadAviso, { useIdentidadCliente } from "./ClienteIdentidadAviso";
import { createPortal } from "react-dom";
import { X, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { toast } from "sonner";

const inputCls =
  "w-full px-3 py-2 text-[13px] rounded-xl border border-gray-200 bg-white focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/20";

export default function ClienteFormModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState({
    cliente: initial?.cliente || "",
    ruc_ci: initial?.ruc_ci || "",
    celular: initial?.celular || "",
    direccion: initial?.direccion || "",
    email: initial?.email || "",
    origen: initial?.origen || "",
    responsable: initial?.responsable || "",
  });
  const [saving, setSaving] = useState(false);
  const identidad = useIdentidadCliente(f.cliente, f.ruc_ci, initial?._id || "");

  const submit = async (e) => {
    e.preventDefault();
    if (!f.cliente.trim()) { toast.error("La razón social del cliente es obligatoria"); return; }
    if (!f.ruc_ci.trim()) { toast.error("El RUC / CI es obligatorio"); return; }
    if (identidad.duplicado) { toast.error("Ese RUC / CI ya está en el maestro de clientes"); return; }
    setSaving(true);
    try {
      let data;
      if (initial?._id) {
        const r = await axios.put(`${API}/listas/clientes/${initial._id}`, f);
        data = r.data;
        toast.success("Cliente actualizado");
      } else {
        const r = await axios.post(`${API}/listas/clientes`, f);
        data = r.data;
        toast.success(`Cliente creado · ${data.codigo}`);
      }
      onSaved?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el cliente");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <h3 className="text-[15px] font-bold text-gray-900">
            {initial ? `Editar Cliente · ${initial.codigo}` : "Nuevo Cliente"}
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500" data-testid="cliente-modal-close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 overflow-y-auto space-y-3" data-testid="cliente-form">
          <div>
            <label className="block text-[11.5px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">
              Cliente (Razón Social) <span className="text-red-500">*</span>
            </label>
            <input className={inputCls} value={f.cliente} autoFocus
              onChange={(e) => setF({ ...f, cliente: e.target.value })}
              placeholder="Ej: Constructora Olimpo S.A."
              data-testid="cliente-input-nombre" />
          </div>
          <ClienteIdentidadAviso {...identidad} onElegir={!initial ? onSaved : undefined} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11.5px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">
                RUC / CI <span className="text-red-500">*</span>
              </label>
              <input className={inputCls} value={f.ruc_ci}
                onChange={(e) => setF({ ...f, ruc_ci: e.target.value })}
                placeholder="80012345-1"
                data-testid="cliente-input-ruc" />
            </div>
            <div>
              <label className="block text-[11.5px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">Celular</label>
              <input className={inputCls} value={f.celular}
                onChange={(e) => setF({ ...f, celular: e.target.value })}
                placeholder="0981 123 456"
                data-testid="cliente-input-celular" />
            </div>
          </div>
          <div>
            <label className="block text-[11.5px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">Dirección</label>
            <input className={inputCls} value={f.direccion}
              onChange={(e) => setF({ ...f, direccion: e.target.value })}
              placeholder="Av. Mcal. López 1234, Asunción"
              data-testid="cliente-input-direccion" />
          </div>
          <div>
            <label className="block text-[11.5px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">Email</label>
            <input type="email" className={inputCls} value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
              placeholder="contacto@cliente.com"
              data-testid="cliente-input-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11.5px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">Origen</label>
              <input className={inputCls} value={f.origen}
                onChange={(e) => setF({ ...f, origen: e.target.value })}
                placeholder="Referido, web, licitación..."
                data-testid="cliente-input-origen" />
            </div>
            <div>
              <label className="block text-[11.5px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">Responsable</label>
              <input className={inputCls} value={f.responsable}
                onChange={(e) => setF({ ...f, responsable: e.target.value })}
                placeholder="Encargado comercial"
                data-testid="cliente-input-responsable" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-xl text-[13px] font-semibold text-gray-600 hover:bg-gray-100">
              Cancelar
            </button>
            <button type="submit" disabled={saving || identidad.duplicado}
              className="px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-[#2E6DA4] hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="cliente-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
